import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Modal } from '@pd/pd-ui-kit';
import { format, subDays } from "date-fns";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useGetAvailabilityReport,
  useGetDealerOfflineReport,
  useGetEmployeesReport,
  useGetIvideonReport,
  useGetSubscriptionsList,
  useGetWorkOrderReport,
} from "~/entity/Ivideon";
import {
  DealersLocationItem,
  useGetDealersLocationItemQuery,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";

import FormReport, { FormReportValuesIProps } from "./FormReport";
import {
  formatToServer,
  LOCALIZE_DEALERS,
  mapToOptions,
  optionReport,
  urlParams,
} from "./options";

import s from "./styles.less";

const ModalReport = ({ sort, state, closeModalSelectReport }) => {
  const [fullForm, setFullForm] = useState(1);
  const language = localStorage.getItem("language");
  const isEn = language === "en";
  const { t } = useTranslation();

  const { notifyReport } = useNotify();
  const { data, isLoading } = useGetSubscriptionsList();

  const validationSchema = yup.object().shape({
    dynamicValues: yup
      .array()
      .when("selectReport", {
        is: (value) => value?.value === 3,
        then: yup.array().min(1, t("validation.required")),
        otherwise: yup.array().notRequired(),
      }),
  });

  const {
    data: fileHash,
    getKeys,
    isError,
    isLoading: isLoadingFileHash,
  } = useGetAvailabilityReport();
  const { getDataReport } = useGetWorkOrderReport(closeModalSelectReport);
  const { getIvideonDataReport } = useGetIvideonReport(closeModalSelectReport);
  const { getDealerOfflineDataReport } = useGetDealerOfflineReport(
    closeModalSelectReport
  );
  const { getEmployeesDataReport } = useGetEmployeesReport(
    closeModalSelectReport
  );

  const { control, handleSubmit, watch, setValue, formState } =
    useForm<FormReportValuesIProps>({
      defaultValues: {
        selectReport: optionReport[0],
        dealers: [],
        localizeDealers: LOCALIZE_DEALERS[0],
        dynamicValues: [],
        startDate: subDays(new Date(), 7),
        endDate: new Date(),
      },
      resolver: yupResolver(validationSchema),
    });

  useEffect(() => {
    const formReport = watch("selectReport");
    setFullForm(formReport.value);
  }, [watch]);

  const dealerCity = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem.City,
    },
  });

  const dealerSubject = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem.Subject,
    },
  });

  const dealerFO = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem.Fo,
    },
  });

  const dealerCountry = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem.Country,
    },
  });

  const selectCity = mapToOptions(
    dealerCity,
    `data.DealerGetter.getDealersLocationItem.${isEn ? "itemsEn" : "items"}`
  );
  const selectSubject = mapToOptions(
    dealerSubject,
    `data.DealerGetter.getDealersLocationItem.${isEn ? "itemsEn" : "items"}`
  );
  const selectFO = mapToOptions(
    dealerFO,
    `data.DealerGetter.getDealersLocationItem.${isEn ? "itemsEn" : "items"}`
  );
  const selectCountry = mapToOptions(
    dealerCountry,
    `data.DealerGetter.getDealersLocationItem.${isEn ? "itemsEn" : "items"}`
  );

  useEffect(() => {
    const currentHash = fileHash;

    if (currentHash) {
      notifyReport(currentHash);
      closeModalSelectReport();
    }
  }, [fileHash]);

  const filterType = watch("localizeDealers")?.value;

  const optionsMap = {
    dealer: data,
    city: selectCity,
    subject: selectSubject,
    FO: selectFO,
    country: selectCountry,
  };

  const currentDynamicOptions = optionsMap[filterType] || [];

  useEffect(() => {
    setValue("dynamicValues", []);
  }, [filterType, setValue]);

  const onSubmit = (data) => {
    const { selectReport } = data;
    if (selectReport?.value === 1) {
      getIvideonDataReport(sort ?? "");
    }
    if (selectReport?.value === 2) {
      const queryString = urlParams(data);
      getKeys(queryString);
    } else if (selectReport?.value === 3) {
      const dataFormat = formatToServer(data);

      getDataReport(dataFormat);
    } else if (selectReport?.value === 4) {
      getDealerOfflineDataReport(sort ?? "");
    } else if (selectReport?.value === 5) {
      getEmployeesDataReport({
        dealerIds: data.dealers?.map((dealer) => String(dealer.value)) ?? [],
      });
    }
  };

  return (
    <Modal
      open={state}
      onCancel={closeModalSelectReport}
      title={'Выберите тип отчёта'}
      centered
    >
      <>
        <FormReport
          control={control}
          onSubmit={handleSubmit(onSubmit)}
          optionReport={optionReport}
          fullForm={fullForm}
          data={data}
          isLoading={isLoading}
          filterType={filterType}
          currentDynamicOptions={currentDynamicOptions}
          closeModalSelectReport={closeModalSelectReport}
          startDate={format(watch("startDate"), "dd.MM.yyyy")}
          endDate={format(watch("endDate"), "dd.MM.yyyy")}
          minDate={watch("startDate")}
          selectReport={watch("selectReport")}
          dynamicValuesError={(formState.errors.dynamicValues as any)?.message}
        />
      </>
    </Modal>
  );
};

export default ModalReport;
